import {
    useState,
    useEffect,
    memo,
} from 'react';
import Button from 'components/UI/Button';
import { useForm } from 'react-hook-form';
import CurrencyInput from 'react-currency-input-field';
import toastAlert from 'components/UI/Alert';
import classNames from 'classnames';
import {
    webLibSdk,
    blockInvalidChar,
} from 'helpers';
import { TRANSACTION_PENDING_MODAL } from '_constants/modalTypes';
import { useModal } from 'hooks';
import StyledSetAllocation from './styles';
import AllocationTypeTable from './AllocationTypeTable';

const StandardSetAllocation = ({ types }) => {
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState('');
    const [remainingAmount, setRemainingAmount] = useState(0);
    const { onOpen: onOpenModal } = useModal();

    const {
        register, setValue, handleSubmit, setError, watch, formState: { errors },
    } = useForm();

    const onFormSubmit = async ({ address, amount, type }) => {
        if (!webLibSdk.isValidAddress(address)) {
            toastAlert({
                type: 'error',
                message: 'Address is not valid',
            });
            return setError('address');
        }

        setLoading(true);
        try {
            const result = await webLibSdk.setAllocation(address, amount, type);
            onOpenModal(TRANSACTION_PENDING_MODAL, { txHash: result.txHash, refetch: () => window.location.reload() });
        } catch (err) {
            toastAlert({
                type: 'error',
                message: 'Allocation was not created',
                customMessage: err,
            });
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name === 'type') {
                const getSelectedType = types.find((x) => x.type === value.type);
                setSelectedType(getSelectedType);
                const amountCalc = getSelectedType.availableAmount - value.amount;
                setRemainingAmount(amountCalc < 0 ? 0 : amountCalc);
            }
            if (name === 'amount') {
                if (value.amount < 0) {
                    setValue('amount', 0);
                }

                const amountCalc = selectedType.availableAmount - (value.amount < 0 ? 0 : value.amount);
                setRemainingAmount(amountCalc < 0 ? 0 : amountCalc);
            }
        });

        return () => subscription.unsubscribe();
    }, [selectedType, types, watch]);

    useEffect(() => {
        setSelectedType(types[0]);
        setRemainingAmount(types[0].availableAmount);
    }, [types]);

    return (
        <StyledSetAllocation
            onSubmit={handleSubmit(onFormSubmit)}
            autoComplete="off">
            <div className="formInputs">
                <div className={classNames('inputGroup addressField', { invalid: errors.address })}>
                    <label htmlFor="">Address</label>
                    <input
                        type="text"
                        placeholder="Enter the address"
                        {...register('address', { required: true })}
                    />
                </div>
                <div className={classNames('inputGroup', { invalid: errors.amount })}>
                    <label htmlFor="">Amount</label>
                    <CurrencyInput
                        name="amount-field"
                        placeholder="Enter the amount"
                        allowNegativeValue={false}
                        onKeyDown={blockInvalidChar}
                        onValueChange={(value = 0) => setValue('amount', value)}
                    />
                </div>
                <div className={classNames('inputGroup', { invalid: errors.type })}>
                    <label htmlFor="">Type</label>
                    <select {...register('type', { required: true })}>
                        {types.map(({ type }) => (
                            <option key={type}>{type}</option>
                        ))}
                    </select>
                </div>
            </div>
            {selectedType && (
                <div className="getAvailableAmount">
                    <div className="allocationTypeTable">
                        <AllocationTypeTable
                            selectedType={{ ...selectedType, remainingAmount }}
                        />
                    </div>

                    <Button
                        loading={loading}
                    >
                        Set Allocation
                    </Button>
                </div>
            )}
        </StyledSetAllocation>
    );
};

export default memo(StandardSetAllocation);
